import React, { useState, useRef, useLayoutEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const COLLAPSE_HEIGHT = 160;

function formatFull(iso) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Mirrors Gmail's own behavior of collapsing long trailing content behind a
// "…" toggle — a full pasted email signature can otherwise be taller than
// the actual reply and bury it off-screen. Quoted reply history (the "On
// ... wrote:" chain Gmail appends to messages composed in Gmail itself) is
// already split out server-side into `quotedHtml`, so it gets its own
// always-collapsed-by-default toggle instead of only hiding once it's tall
// enough to trip the height check below.
export default function MessageBubble({ message }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [quoteExpanded, setQuoteExpanded] = useState(false);
  const bodyRef = useRef(null);

  useLayoutEffect(() => {
    if (bodyRef.current) {
      setIsOverflowing(bodyRef.current.scrollHeight > COLLAPSE_HEIGHT + 20);
    }
  }, [message.bodyHtml, message.bodyText]);

  const collapsible = isOverflowing && !expanded;

  return (
    <div className={`cinbox-bubble-row ${message.direction}`}>
      <div className="cinbox-bubble">
        <div className="cinbox-bubble-meta">
          <span className="cinbox-bubble-from">
            {message.direction === "outbound" ? "You" : message.fromName || message.fromEmail}
          </span>
          <span className="cinbox-bubble-time">{formatFull(message.receivedAt)}</span>
        </div>

        <div
          className={`cinbox-bubble-body-wrap${collapsible ? " collapsed" : ""}`}
          style={collapsible ? { maxHeight: COLLAPSE_HEIGHT } : undefined}
        >
          <div
            className="cinbox-bubble-body"
            ref={bodyRef}
            dangerouslySetInnerHTML={{ __html: message.bodyHtml || `<p>${message.bodyText || ""}</p>` }}
          />
          {collapsible && <div className="cinbox-bubble-fade" />}
        </div>

        {isOverflowing && (
          <button className="cinbox-bubble-toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <><ExpandLessIcon fontSize="inherit" /> Show less</> : <><ExpandMoreIcon fontSize="inherit" /> Show more</>}
          </button>
        )}

        {message.quotedHtml && (
          <>
            <button className="cinbox-bubble-toggle cinbox-bubble-quote-toggle" onClick={() => setQuoteExpanded((v) => !v)}>
              {quoteExpanded ? <><ExpandLessIcon fontSize="inherit" /> Hide quoted text</> : <><ExpandMoreIcon fontSize="inherit" /> Show quoted text</>}
            </button>
            {quoteExpanded && (
              <div className="cinbox-bubble-quoted" dangerouslySetInnerHTML={{ __html: message.quotedHtml }} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
